import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { SectionCard } from "../../components/admin/AdminCard";
import toast from "react-hot-toast";

type Bucket = { key: string; count: number };
type Day = { day: string; pageviews: number; visitors: number };

type Overview = {
  range: { start: string; end: string; site: string | null };
  totals: {
    pageviews: number;
    visitors: number;
    sessions: number;
    avg_duration_ms: number;
    logged_in_pageviews: number;
  };
  daily: Day[];
  pages: Bucket[];
  referrers: Bucket[];
  utm: { sources: Bucket[]; mediums: Bucket[]; campaigns: Bucket[] };
  devices: Bucket[];
  browsers: Bucket[];
  oses: Bucket[];
  countries: Bucket[];
  sites: Bucket[];
};

const SITE_LABEL: Record<string, string> = {
  app: "app.sportiveducat.ro",
  www: "sportiveducat.ro",
};

const RANGES: { key: string; label: string; days: number }[] = [
  { key: "7", label: "7 zile", days: 7 },
  { key: "30", label: "30 zile", days: 30 },
  { key: "90", label: "90 zile", days: 90 },
];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function humanDuration(ms: number): string {
  if (!ms) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

/** ISO-3166 alpha-2 → a readable name for the handful we actually see. */
const COUNTRY: Record<string, string> = {
  RO: "România",
  MD: "R. Moldova",
  IT: "Italia",
  ES: "Spania",
  DE: "Germania",
  GB: "Marea Britanie",
  FR: "Franța",
  US: "SUA",
  AT: "Austria",
  NL: "Olanda",
  BE: "Belgia",
  HU: "Ungaria",
  BG: "Bulgaria",
};

function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="p-4 bg-white border shadow-sm border-slate-200 rounded-2xl">
      <p className="text-xs font-semibold tracking-wider uppercase text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-800 tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

/**
 * Horizontal bar list — the shape every breakdown here takes.
 *
 * Bars are scaled against the largest row rather than the total, so a list
 * where one entry dominates still shows the relative size of the rest.
 */
function BarList({
  rows,
  empty,
  label,
  formatKey,
}: {
  rows: Bucket[];
  empty: string;
  label?: string;
  formatKey?: (k: string) => string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  const total = rows.reduce((s, r) => s + r.count, 0) || 1;

  if (rows.length === 0) return <p className="px-5 py-4 text-sm text-slate-400">{empty}</p>;

  return (
    <div className="divide-y divide-slate-50">
      {rows.map((r) => (
        <div key={`${label}-${r.key}`} className="relative px-5 py-2.5">
          <div
            className="absolute inset-y-0 left-0 bg-indigo-50 rounded-r"
            style={{ width: `${(r.count / max) * 100}%` }}
            aria-hidden
          />
          <div className="relative flex items-center gap-3">
            <span className="flex-1 text-sm truncate text-slate-700" title={r.key}>
              {formatKey ? formatKey(r.key) : r.key}
            </span>
            <span className="text-xs text-slate-400 tabular-nums">
              {Math.round((r.count / total) * 100)}%
            </span>
            <span className="text-sm font-semibold text-slate-800 tabular-nums w-14 text-right">
              {r.count.toLocaleString("ro-RO")}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Daily traffic as an inline SVG area chart.
 *
 * Deliberately hand-drawn rather than pulling in a charting library: two
 * series over at most 90 points does not justify the bundle, and this keeps
 * the admin build the size it is today.
 */
function TrafficChart({ daily }: { daily: Day[] }) {
  const W = 1000;
  const H = 220;
  const PAD = 8;

  if (daily.length === 0) {
    return <p className="px-5 py-8 text-sm text-center text-slate-400">Niciun eveniment în interval.</p>;
  }

  const max = Math.max(1, ...daily.map((d) => d.pageviews));
  const stepX = daily.length > 1 ? (W - PAD * 2) / (daily.length - 1) : 0;
  const x = (i: number) => PAD + i * stepX;
  const y = (v: number) => H - PAD - (v / max) * (H - PAD * 2);

  const line = (pick: (d: Day) => number) =>
    daily.map((d, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(pick(d)).toFixed(1)}`).join(" ");

  const area = `${line((d) => d.pageviews)} L${x(daily.length - 1).toFixed(1)},${H - PAD} L${x(0).toFixed(1)},${H - PAD} Z`;

  // At most a handful of date labels, whatever the range length.
  const labelEvery = Math.max(1, Math.ceil(daily.length / 7));

  return (
    <div className="px-5 py-4">
      <div className="flex items-center gap-4 mb-3 text-xs font-medium text-slate-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-1 rounded bg-indigo-500" /> Vizualizări
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-1 rounded bg-emerald-500" /> Vizitatori
        </span>
        <span className="ml-auto text-slate-400">max {max.toLocaleString("ro-RO")} / zi</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[220px]" preserveAspectRatio="none">
        <path d={area} fill="rgb(99 102 241 / 0.10)" />
        <path d={line((d) => d.pageviews)} fill="none" stroke="rgb(99 102 241)" strokeWidth={2} vectorEffect="non-scaling-stroke" />
        <path d={line((d) => d.visitors)} fill="none" stroke="rgb(16 185 129)" strokeWidth={2} vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="flex justify-between mt-1 text-[10px] text-slate-400">
        {daily
          .map((d, i) => ({ d, i }))
          .filter(({ i }) => i % labelEvery === 0)
          .map(({ d }) => (
            <span key={d.day}>{d.day.slice(5).replace("-", ".")}</span>
          ))}
      </div>
    </div>
  );
}

/**
 * Traffic and usage for both properties: the app and the presentation site.
 *
 * The data is our own — no third-party tag, no cookie, and the visitor key is
 * a salted hash that rolls over daily, so this measures audience size without
 * being able to follow anyone across days.
 */
export default function Analytics() {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState<"" | "app" | "www">("");
  const [rangeKey, setRangeKey] = useState("30");

  const range = useMemo(() => {
    const days = RANGES.find((r) => r.key === rangeKey)?.days ?? 30;
    const end = new Date();
    const start = new Date(end.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
    return { start: ymd(start), end: ymd(end) };
  }, [rangeKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<Overview>("/web-analytics/overview", {
        params: { site: site || undefined, start: range.start, end: range.end },
      });
      setData(data);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-au putut încărca datele.");
    }
    setLoading(false);
  }, [site, range.start, range.end]);

  useEffect(() => {
    load();
  }, [load]);

  const snippet = `<script defer src="${(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}/web-analytics/script.js?site=www"></script>`;

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Cod copiat.");
    } catch {
      toast.error("Copiază manual codul de mai jos.");
    }
  };

  const t = data?.totals;
  const pagesPerSession = t && t.sessions ? (t.pageviews / t.sessions).toFixed(1) : "—";
  const loggedInShare =
    t && t.pageviews ? `${Math.round((t.logged_in_pageviews / t.pageviews) * 100)}% autentificat` : undefined;

  const tabClass = (active: boolean) =>
    `px-3.5 py-2 text-sm font-semibold rounded-xl transition ${
      active ? "bg-indigo-600 text-white shadow-sm" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
    }`;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6">
      <AdminPageHeader
        title="Analytics"
        subtitle="Trafic și utilizare pe app.sportiveducat.ro și sportiveducat.ro."
        actions={
          <AdminButton variant="secondary" onClick={load}>
            Reîmprospătează
          </AdminButton>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={tabClass(site === "")} onClick={() => setSite("")}>
          Ambele
        </button>
        <button type="button" className={tabClass(site === "app")} onClick={() => setSite("app")}>
          Aplicație (app.sportiveducat.ro)
        </button>
        <button type="button" className={tabClass(site === "www")} onClick={() => setSite("www")}>
          Site (sportiveducat.ro)
        </button>
        <div className="flex gap-2 ml-auto">
          {RANGES.map((r) => (
            <button key={r.key} type="button" className={tabClass(rangeKey === r.key)} onClick={() => setRangeKey(r.key)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <p className="text-sm text-slate-500">Se încarcă...</p>
      ) : !data ? (
        <p className="text-sm text-slate-500">Nu există date.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Vizualizări" value={data.totals.pageviews.toLocaleString("ro-RO")} hint={loggedInShare} />
            <Stat label="Vizitatori" value={data.totals.visitors.toLocaleString("ro-RO")} hint="unici / zi" />
            <Stat label="Sesiuni" value={data.totals.sessions.toLocaleString("ro-RO")} />
            <Stat label="Pagini / sesiune" value={pagesPerSession} />
            <Stat label="Timp mediu / pagină" value={humanDuration(data.totals.avg_duration_ms)} />
          </div>

          {data.totals.pageviews === 0 && (
            <div className="px-4 py-3 text-sm border rounded-xl bg-amber-50 border-amber-200 text-amber-800">
              Încă nu s-a înregistrat niciun eveniment în acest interval. Aplicația raportează automat, iar pentru
              sportiveducat.ro trebuie adăugat scriptul de mai jos în pagină.
            </div>
          )}

          <SectionCard title="Trafic zilnic" subtitle={`${range.start} → ${range.end}`}>
            <TrafficChart daily={data.daily} />
          </SectionCard>

          {site === "" && data.sites.length > 0 && (
            <SectionCard title="Pe proprietate" subtitle="Cum se împarte traficul între cele două site-uri">
              <BarList rows={data.sites} label="sites" empty="—" formatKey={(k) => SITE_LABEL[k] || k} />
            </SectionCard>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <SectionCard title="Pagini vizitate" subtitle="Identificatorii din URL sunt grupați ca :id">
              <BarList rows={data.pages} label="pages" empty="Nicio pagină înregistrată." />
            </SectionCard>

            <SectionCard title="De unde vine traficul" subtitle="Referrer extern; navigarea internă nu apare aici">
              <BarList
                rows={data.referrers}
                label="ref"
                empty="Tot traficul a venit direct (link tastat, favorite, aplicație sau referrer ascuns)."
              />
            </SectionCard>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <SectionCard title="Sursă campanie" subtitle="utm_source">
              <BarList rows={data.utm.sources} label="utm_s" empty="Niciun utm_source." />
            </SectionCard>
            <SectionCard title="Mediu" subtitle="utm_medium">
              <BarList rows={data.utm.mediums} label="utm_m" empty="Niciun utm_medium." />
            </SectionCard>
            <SectionCard title="Campanie" subtitle="utm_campaign">
              <BarList rows={data.utm.campaigns} label="utm_c" empty="Nicio utm_campaign." />
            </SectionCard>
          </div>

          <div className="grid gap-6 lg:grid-cols-4">
            <SectionCard title="Dispozitiv">
              <BarList rows={data.devices} label="dev" empty="—" />
            </SectionCard>
            <SectionCard title="Browser">
              <BarList rows={data.browsers} label="br" empty="—" />
            </SectionCard>
            <SectionCard title="Sistem de operare">
              <BarList rows={data.oses} label="os" empty="—" />
            </SectionCard>
            <SectionCard title="Țară">
              <BarList
                rows={data.countries}
                label="co"
                empty="Nedisponibil — țara vine de la CDN-ul din fața aplicației."
                formatKey={(k) => COUNTRY[k] || k}
              />
            </SectionCard>
          </div>

          <SectionCard
            title="Cod de instalare pentru sportiveducat.ro"
            subtitle="Site-ul de prezentare este separat de aplicație — adaugă scriptul o singură dată, înainte de </body>."
            actions={
              <AdminButton variant="secondary" onClick={copySnippet}>
                Copiază
              </AdminButton>
            }
            bodyPadded
          >
            <pre className="p-3 overflow-x-auto text-xs rounded-lg bg-slate-900 text-slate-100">{snippet}</pre>
            <p className="mt-3 text-xs text-slate-500">
              Aplicația raportează deja singură, fără script. Nu se scriu cookie-uri și nu se stochează adresa IP:
              un vizitator este un hash cu sare, recalculat zilnic — util pentru volum, imposibil de urmărit de la o zi
              la alta.
            </p>
          </SectionCard>
        </>
      )}
    </div>
  );
}
